import {
  Fragment,
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import {
  addDays,
  HORIZON_FROM,
  MILESTONES,
  TRAMOS,
  type ProgressionState,
} from '@selflevel/domain';
import { Marca } from '@selflevel/ui';
import { useAreaMap, useHeatmap, useProgression, useToday } from '../store/hooks';
import { Bar, Card, Insignia, Sello, fechaLarga, num } from '../ui/primitivas';
import { Divisa } from '../ui/iconos';
import { hastaHito, indiceDeTramo, profundidad, profundidadMedia, tramoAlcanzado } from './camino';
/**
 * La rueda de la vida y tu crecimiento llegan aparte: están más abajo en la pantalla
 * y su código (cuestionario, dibujo, gráficas) no tiene por qué pesar al abrir la app.
 */
const TarjetaRueda = lazy(() => import('./rueda-vida').then((m) => ({ default: m.TarjetaRueda })));
const TarjetaCrecimiento = lazy(() =>
  import('./crecimiento').then((m) => ({ default: m.TarjetaCrecimiento })),
);

/**
 * PROGRESO — el sitio donde se ve en qué te estás convirtiendo.
 *
 * Una sola piel: el dial con un arco por área, los sellos acuñados y el camino
 * en fuga. NO pasa la prueba de la reunión, y es deliberado: su criterio es
 * «¿esto hace que quiera abrir la app mañana?», con un solo límite — nada que
 * huela a monetización. Sin monedas, sin gemas, sin confeti, sin mascotas y sin
 * cuentas atrás.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * HUBO UNA SEGUNDA PIEL, «sobria», y se retiró el 21 de septiembre de 2026.
 *
 * La idea era buena sobre el papel: el mismo motor contado en filas de 44
 * monocromas, para quien abre la app en una reunión. En pantalla no era eso.
 * Era la misma información con menos trabajo encima —sin dial, sin camino, sin
 * sellos—, y el usuario no leía «otra voz»: leía la versión a medio hacer.
 * Una alternativa que parece un borrador no es una alternativa, es una duda.
 *
 * Y la duda salía cara: cada pantalla llevaba su `if`, cada token tenía que
 * valer para dos estéticas que el paso 7B obligaba a que NO se parecieran, y
 * cada cosa nueva había que diseñarla dos veces. Con una sola piel, lo que se
 * gana no es código: es que el trabajo entero va a una.
 *
 * La accesibilidad NO dependía de esto y sigue donde estaba: `prefers-reduced-
 * motion` apaga el movimiento, el modo sólido apaga el cristal en equipos
 * flojos, y el tema claro y el oscuro siguen siendo dos.
 * ───────────────────────────────────────────────────────────────────────────
 */
export function Progreso(): ReactElement {
  const progression = useProgression();
  const areas = useAreaMap();
  const today = useToday();
  const desde = addDays(today, -180);
  const heat = useHeatmap(desde, today);

  if (!progression) return <main className="screen" />;

  return (
    <main className="screen screen--wide">
      <header className="topbar glass-2">
        <div className="row">
          <Divisa tramo={tramoAlcanzado(progression).key} size={22} />
          <span className="t-title-3">{tramoAlcanzado(progression).name}</span>
        </div>
      </header>

      <CabeceraExpresiva estado={progression} areas={areas} />

      <TiraTramos estado={progression} />

      {/* En escritorio, lo que cambia despacio a un lado y lo que se mira a
          otro: la temporada y las áreas son cifras que se consultan; el camino
          es la pantalla larga por la que se baja. El orden del DOM es el de
          móvil —que no cambia— y la rejilla lo reparte en 1.024. */}
      <div className="cols cols--invertida">
        <div className="cols__lado">
          <TarjetaTemporada estado={progression} />

          <Card title="Tus áreas">
            <ul className="stack stack--tight">
              {progression.dimensions.length === 0 ? (
                <li className="t-sm t-dim">Marca algo y esto se llena solo.</li>
              ) : null}
              {progression.dimensions.map((dimension) => {
                const area = areas.get(dimension.key);
                return (
                  <li
                    key={dimension.key}
                    className="dim-row"
                    style={{ ['--area']: `var(${area?.colorToken ?? '--dim-1'})` } as CSSProperties}
                  >
                    <span className="habit__disc" style={{ width: 28, height: 28 }}>
                      <Marca id={area?.marca ?? 'orden'} size={16} polarity="area" />
                    </span>
                    <div className="grow stack stack--tight">
                      <div className="row row--between">
                        <span className="t-sm t-body-strong">{area?.name ?? 'Área retirada'}</span>
                        <span className="t-sm t-dim tnum">nivel {dimension.level.level}</span>
                      </div>
                      <Bar
                        progress={dimension.level.progress}
                        color={area?.colorToken ?? '--dim-1'}
                        label={`${area?.name ?? 'Área retirada'}: nivel ${dimension.level.level}`}
                      />
                    </div>
                    <span className="t-sm t-dim tnum">{num(dimension.points)}</span>
                  </li>
                );
              })}
            </ul>
          </Card>

          <Suspense fallback={null}>
            <TarjetaRueda hoy={today} />
          </Suspense>
        </div>

        <div>
          {/* El camino NO va dentro de una tarjeta. Un recuadro con título lo
              convierte en un módulo más de la pantalla, y lo que tiene que ser
              es la pantalla: la pista corre de arriba abajo y el umbral la
              cruza de lado a lado. Encerrarlo en una caja de 16 px de radio le
              pone un marco a algo que va a sangre. */}
          <section className="stack">
            <div className="section-head" style={{ marginBottom: 0 }}>
              <h2 className="t-title-3">El camino</h2>
              <span className="t-sm t-dim tnum">
                {progression.unlocked.length} de {MILESTONES.length}
              </span>
            </div>
            <Camino estado={progression} areas={areas} />
          </section>

          <Card
            title="Los últimos seis meses"
            action={
              <span className="t-sm t-dim">{num(progression.totalPoints)} puntos en total</span>
            }
          >
            <MapaDeCalor dias={heat ?? []} />
          </Card>

          <Suspense fallback={null}>
            <TarjetaCrecimiento hoy={today} />
          </Suspense>
        </div>
      </div>
    </main>
  );
}

// ===========================================================================

/**
 * La cabecera expresiva es **el dial**: un arco por área, cada uno en su tinte y
 * relleno a su propio progreso, con la media general en la escala exterior.
 *
 * El anillo de antes decía «vas por tres cuartos del nivel 4». Correcto y
 * anodino: la misma cifra estaba dos renglones más abajo escrita en letras. El
 * dial dice algo que no estaba en ningún otro sitio de la pantalla —**qué parte
 * de tu vida va lanzada y cuál está parada**— y lo dice sin texto y sin
 * desplegar la tarjeta de áreas. Por eso es el héroe y por eso gana color: es
 * el único sitio donde los seis tintes cuentan algo de un vistazo.
 *
 * Con cero puntos sigue siendo un objeto: los surcos vacíos y la escala son
 * forma. Ese era el otro fallo de la versión anterior —a estrenar, la piel
 * expresiva no tenía nada encendido que enseñar.
 */
function CabeceraExpresiva({
  estado,
  areas,
}: {
  estado: ProgressionState;
  areas: Map<string, { colorToken: string }>;
}): ReactElement {
  // Los arcos salen de TUS ÁREAS, no de `dimensions`. No es un detalle: una
  // dimensión solo existe cuando ya ha entrado algún punto, así que a estrenar
  // la lista viene vacía y el dial volvía a ser el anillo gris de antes justo
  // el día en que más falta hace que la pantalla enseñe algo. Las áreas existen
  // desde el alta, y un arco a cero es información —«esta no la has tocado»—,
  // no un hueco.
  const porArea = new Map(estado.dimensions.map((d) => [d.key, d]));
  const arcos = [...areas].map(([id, area]) => ({
    token: area.colorToken,
    progress: porArea.get(id)?.level.progress ?? 0,
  }));

  return (
    <section className="level level--dial">
      <Insignia
        size={132}
        nivel={estado.generalLevel}
        progress={estado.generalMean % 1}
        segmentos={estado.dimensions.length}
        arcos={arcos}
        escala
        label={`Nivel general ${estado.generalLevel}`}
      />
      <div className="stack stack--tight">
        <p className="t-caption t-dim">Nivel general</p>
        <p className="t-title-2">media {estado.generalMean.toFixed(1).replace('.', ',')}</p>
        <p className="t-sm t-dim">
          {num(estado.totalPoints)} puntos de por vida
          {estado.nextMilestone
            ? ` · ${num(estado.toNextMilestone)} para ${estado.nextMilestone.name}`
            : ''}
        </p>
      </div>
    </section>
  );
}

/** La tira de tramos: se ve hasta Cénit desde el día uno. */
function TiraTramos({ estado }: { estado: ProgressionState }): ReactElement {
  // El tramo alcanzado, no el de los puntos de ahora: no retrocede (ver `camino`).
  const alcanzado = tramoAlcanzado(estado);
  const actual = indiceDeTramo(alcanzado.key);
  const falta = alcanzado.to === null ? 0 : Math.max(0, alcanzado.to - estado.totalPoints);

  return (
    <div className="tramos" role="list" aria-label="Tramos del camino">
      {TRAMOS.map((tramo, index) => {
        const estadoTramo = index < actual ? 'past' : index === actual ? 'current' : 'future';
        return (
          <div key={tramo.key} className={`tramo tramo--${estadoTramo}`} role="listitem">
            <span className="tramo__badge">
              <Divisa tramo={tramo.key} size={index === actual ? 22 : 18} />
            </span>
            <span className="t-caption">{tramo.name}</span>
            {index === actual && alcanzado.to !== null ? (
              <span className="t-caption t-dim tnum">{num(falta)}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function TarjetaTemporada({ estado }: { estado: ProgressionState }): ReactElement {
  const { season } = estado;

  return (
    <Card>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <Sello fill={season.liga.fill} size={44} label={`Liga ${season.liga.name}`} />
        <div className="grow stack stack--tight">
          <p className="t-caption t-dim">Temporada · {season.label}</p>
          <p className="t-title-2">{season.liga.name}</p>
          <Bar
            progress={season.next ? 1 - season.toNext / (season.next.from - season.liga.from) : 1}
          />
          <p className="t-sm t-dim">
            {num(season.points)} puntos
            {season.next ? ` · ${num(season.toNext)} para ${season.next.name}` : ' · liga máxima'}
          </p>
          {/* Sin cuenta atrás, nunca: produce atracón. La fecha se consulta. */}
          <p className="t-sm t-dim">Cierra el {fechaLarga(season.to)}. No se resta nada.</p>
        </div>
      </div>
    </Card>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL CAMINO.
 *
 * Era una serpentina de nodos con carriles de distinto sangrado, y se leía como
 * una lista descuadrada: se veía QUE había un orden, pero no POR DÓNDE ibas.
 *
 * Ahora es lo que la maqueta pedía y lo que el dato ya decía: **una pista
 * vertical partida por un umbral**. Debajo, lo andado, en una cinta de acento
 * ancha con sus piezas encima. Encima, lo que viene, en un hilo fino. Y en la
 * línea que las separa, dónde estás.
 *
 * Tres decisiones que sostienen la lectura:
 *
 *  · **Va de abajo arriba.** Lo alcanzado se acumula al pie y el futuro sube.
 *    Al revés —lo conseguido arriba— el camino se lee como una cuenta atrás.
 *
 *  · **La palabra vertical del umbral es el TRAMO que viene**, no un rótulo
 *    decorativo. El umbral de verdad de esta app no es el hito siguiente: es el
 *    territorio al que pasas. Así la pieza nombra algo en vez de adornar.
 *
 *  · **Los rótulos alternan lado.** No es simetría: con todos al mismo lado la
 *    pista queda pegada a un borde y el ojo deja de seguirla. Alternando, la
 *    pista es el eje y el texto cuelga de ella.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * LA LUZ Y LA PROFUNDIDAD.
 *
 * Todo lo de arriba seguía siendo cierto y la pantalla seguía viéndose sobria,
 * porque le faltaban dos cosas que no son de composición sino de escena:
 *
 *  · **Una fuente de luz.** Ahora la hay, al pie y detrás de todo: la pista se
 *    enciende desde abajo y lo lejano se disuelve en bruma. Es atmósfera, no
 *    brillo: no hay una sola sombra de color encima de un texto.
 *
 *  · **Un eje Z.** Cada nodo lleva `--z` —0 arriba, lo más lejos; 1 al pie, lo
 *    más cerca— y de ahí salen las tres cosas que dan perspectiva: la cinta se
 *    ensancha al acercarse, las piezas futuras menguan, y lo que está lejos se
 *    apaga. El nodo ACTUAL se queda fuera del apagado, siempre: es el único que
 *    tiene que encontrarte el ojo sin leer.
 *
 *  · **El suelo de salida.** El fallo más caro de la versión anterior: la cinta
 *    ámbar solo existía si ya habías alcanzado hitos, así que el día uno el
 *    único dispositivo expresivo de la pantalla era invisible y la piel
 *    expresiva era la sobria con círculos más grandes. Sin nada alcanzado se
 *    pinta un tramo encendido al pie —el suelo donde estás de pie, no un logro
 *    que no has hecho— y el camino ya se lee como camino desde el minuto cero.
 * ───────────────────────────────────────────────────────────────────────────
 * ═══════════════════════════════════════════════════════════════════════════
 */
function Camino({
  estado,
  areas,
}: {
  estado: ProgressionState;
  areas: Map<string, { colorToken: string }>;
}): ReactElement {
  const alcanzados = new Set(estado.unlocked.map((m) => m.key));
  const siguiente = estado.nextMilestone;
  const colores = [...areas.values()].map((a) => a.colorToken);

  // SE SUBE AL CAMINO A PROPÓSITO, Y ESO ARREGLA UNA TRAMPA.
  //
  // El camino era un contenedor de scroll propio con `overscroll-behavior:
  // contain`, y en un móvil eso es una trampa: mide 78vh, así que el dedo cae
  // dentro casi siempre, y el `contain` impide que el scroll salte a la página
  // al llegar al tope. Quien quisiera pasar de largo hasta el mapa de calor se
  // quedaba encerrado recorriendo 3.800 px de hitos.
  //
  // El arreglo no es aflojar el `contain`: es que no haya scroll anidado. Por
  // defecto se ven los alcanzados y los cinco siguientes —corto, en el flujo de
  // la página, imposible de atrapar— y subir a la cima es un gesto explícito
  // que despliega los veinticinco EN LA PÁGINA. Un solo eje de scroll siempre.
  //
  // Y se decide con estado, no con una media query en JS: `useMediaQuery` es
  // justo lo que el armazón de esta app presume de no tener, y aquí no hace
  // falta porque la respuesta correcta es la misma en móvil y en escritorio.
  const [abierto, setAbierto] = useState(false);
  const hasta = hastaHito(siguiente?.n ?? null, abierto, MILESTONES.length);

  // Al revés: el futuro arriba, lo andado al pie.
  const orden = MILESTONES.slice(0, hasta).reverse();
  const primerAlcanzado = orden.findIndex((hito) => alcanzados.has(hito.key));

  const umbral = (
    <div className="camino__umbral">
      <span className="camino__umbral-nombre" aria-hidden="true">
        Umbral
      </span>
      <span className="camino__umbral-nivel">
        <span className="t-caption t-dim">Tu nivel</span>
        <span className="camino__nivel tnum">{estado.generalLevel}</span>
      </span>
    </div>
  );

  return (
    <div className={`camino${abierto ? ' camino--abierto' : ''}`}>
      {/* LA PUERTA DE ARRIBA. El camino va de abajo arriba, así que lo que hay
          más allá está por ENCIMA: el control de subir tiene que estar donde
          está lo que promete, no al pie. Plegado dice cuántos hitos faltan por
          ver; desplegado, cómo volver. */}
      {abierto ? null : (
        <button
          type="button"
          className="camino__puerta"
          data-abierto="false"
          onClick={() => setAbierto(true)}
        >
          <span className="camino__puerta-flecha" aria-hidden="true">
            <Divisa tramo="salto" size={18} />
          </span>
          <span className="t-body-strong">Subir a la cima</span>
          <span className="t-sm t-dim tnum">{MILESTONES.length - hasta} hitos más</span>
        </button>
      )}

      {/* LA CIMA. Va arriba del todo porque es lo más lejos que se ve, y NO es
          un nodo: el camino termina donde termina el catálogo, y encima se dice
          que sigue. */}
      {abierto ? (
        <>
          <button
            type="button"
            className="camino__puerta"
            data-abierto="true"
            onClick={() => setAbierto(false)}
          >
            <span className="camino__puerta-flecha" aria-hidden="true">
              <Divisa tramo="salto" size={18} />
            </span>
            <span className="t-body-strong">Plegar el camino</span>
          </button>
          <div className="horizonte">
            <Divisa tramo="vuelo" size={22} />
            <span className="grow t-sm">Vuelo · el camino sigue · desde {num(HORIZON_FROM)}</span>
          </div>
        </>
      ) : null}

      {orden.map((hito, index) => {
        const alcanzado = alcanzados.has(hito.key);
        const actual = siguiente?.key === hito.key;
        const cierre = hito.n === 10 || hito.n === 25;
        const color = colores[index % Math.max(1, colores.length)] ?? '--dim-1';
        // Profundidad: 0 arriba (lo más lejos), 1 al pie (lo más cerca).
        //
        // Van DOS por fila, el borde de arriba y el de abajo, y no una. Con una
        // sola, cada tramo de pista es un rectángulo de ancho constante y lo que
        // se ve es una escalera de peldaños cada vez más anchos. Con las dos,
        // cada tramo es un trapecio que encaja exacto con el de arriba y el de
        // abajo —z1 de una fila ES z0 de la siguiente— y los tramos sueltos se
        // sueldan en UNA rampa continua en fuga. Esa es la diferencia entre
        // «barra que crece» y «camino que viene hacia ti».
        //
        // Y SALEN DEL CATÁLOGO ENTERO, no de cuántas filas se estén pintando.
        // Dividir por `orden.length` parecía lo natural y tenía un efecto feo:
        // al desplegar, las filas que YA estaban en pantalla cambiaban de
        // profundidad —el hito 1 pasaba de z 0,83 a 0,96— y con ella de ancho y
        // de alto. O sea que abrir el camino re-proporcionaba de golpe el trozo
        // que estabas mirando. Contra el catálogo, la profundidad de un hito es
        // la que le toca por dónde cae en el camino y no cambia nunca: al abrir
        // solo APARECEN filas nuevas por arriba, y ninguna de las viejas se
        // mueve. Además es más verdad: la lejanía de un hito no depende de
        // cuánto se esté enseñando.
        const { z0, z1 } = profundidad(hito.n, MILESTONES.length);

        return (
          <Fragment key={hito.key}>
            {index === primerAlcanzado ? umbral : null}
            <div
              className={[
                'camino__nodo',
                alcanzado ? 'camino__nodo--alcanzado' : actual ? 'camino__nodo--actual' : '',
                cierre ? 'camino__nodo--cierre' : '',
                index % 2 === 0 ? 'camino__nodo--der' : 'camino__nodo--izq',
              ]
                .filter(Boolean)
                .join(' ')}
              style={
                {
                  ['--area']: `var(${color})`,
                  ['--z0']: z0.toFixed(4),
                  ['--z1']: z1.toFixed(4),
                  ['--z']: profundidadMedia(hito.n, MILESTONES.length).toFixed(4),
                } as CSSProperties
              }
            >
              <span className="camino__pieza">
                {cierre ? (
                  <Divisa tramo={hito.n === 10 ? 'paso' : 'salto'} size={26} />
                ) : (
                  <span className="camino__cifra tnum">{hito.n}</span>
                )}
              </span>
              <span className="camino__texto">
                <span className="camino__nombre">{hito.name}</span>
                <span className="camino__abre">{hito.unlocks}</span>
              </span>
              {actual ? (
                <span className="camino__falta">
                  <span className="camino__falta-cifra tnum">{num(estado.toNextMilestone)}</span>
                  <span className="t-caption t-dim">puntos</span>
                </span>
              ) : null}
            </div>
          </Fragment>
        );
      })}

      {/* Sin ningún hito alcanzado el umbral no ha aparecido dentro del bucle:
          está todo por delante, así que la línea va al pie. */}
      {primerAlcanzado === -1 ? umbral : null}

      {/* EL SUELO, SIEMPRE. Al principio solo se pintaba cuando no había nada
          alcanzado, y con hitos ya ganados la rampa moría en un corte recto
          justo debajo del último sello: un camino que se acaba de golpe a los
          pies del que lo mira. Es la misma pieza en los dos casos y dice lo
          mismo —el suelo donde estás de pie, que continúa fuera de cuadro—;
          lo único que cambia es si tiene hitos encima o todavía no. */}
      <div className="camino__suelo" aria-hidden="true" />
    </div>
  );
}

function MapaDeCalor({ dias }: { dias: { localDate: string; points: number }[] }): ReactElement {
  const caja = useRef<HTMLDivElement>(null);

  // Al final, no al principio. Un mapa de calor que abre por hace seis meses
  // enseña justo lo que al usuario no le interesa: lo de hoy está en el otro
  // extremo y hay que buscarlo.
  useEffect(() => {
    const el = caja.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [dias.length]);

  if (dias.length === 0) {
    return <p className="t-sm t-dim">Necesitas más registros. Esto se llena solo.</p>;
  }

  const nivel = (points: number) => {
    if (points === 0) return 0;
    if (points < 20) return 1;
    if (points < 45) return 2;
    if (points < 90) return 3;
    return 4;
  };

  return (
    <div
      className="heat"
      ref={caja}
      role="img"
      aria-label="Mapa de calor de los últimos seis meses"
    >
      {dias.map((dia) => (
        <span
          key={dia.localDate}
          className="heat__cell"
          data-level={nivel(dia.points)}
          title={`${dia.localDate}: ${dia.points} puntos`}
        />
      ))}
    </div>
  );
}
